import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Filter, Grid, List } from 'lucide-react';
import { publicApi } from '@/services/api';
import { resolveImage, PRODUCT_IMAGE_BASE } from '@/lib/imageUrl';

const ProductsV2 = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, selectedCategory, products]);

  const loadProducts = async () => {
    try {
      const response = await publicApi.getProducts();
      setProducts(response.data);
      const cats = [...new Set(response.data.map(p => p.categoria).filter(Boolean))];
      setCategories(cats);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;
    
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory) {
      filtered = filtered.filter(p => p.categoria === selectedCategory);
    }
    
    setFilteredProducts(filtered);
  };

  return (
    <div className="bg-v2-darker min-h-screen">
      {/* Hero Banner */}
      <div className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/productos-header.jpg"
            alt="Productos"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-v2-darker/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-12 h-0.5 bg-v2-orange" />
            <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Catálogo</span>
            <span className="w-12 h-0.5 bg-v2-orange" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Nuestros <span className="text-v2-orange">Productos</span>
          </h1>
          <p className="text-xl text-v2-text-muted max-w-2xl mx-auto">
            Catálogo completo de aceros en todas sus formas, medidas y espesores
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-20 z-30 bg-v2-dark border-y border-v2-border py-4">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="w-full lg:w-96 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-v2-text-muted" size={20} />
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-v2-gray border border-v2-border rounded text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors"
                data-testid="search-input-v2"
              />
            </div>

            <div className="flex items-center gap-4 w-full lg:w-auto">
              {/* Category Filter */}
              <div className="flex-1 lg:flex-none flex items-center gap-3">
                <Filter size={20} className="text-v2-text-muted hidden sm:block" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="flex-1 lg:w-48 px-4 py-3 bg-v2-gray border border-v2-border rounded text-white focus:border-v2-orange focus:outline-none transition-colors"
                  data-testid="category-filter-v2"
                >
                  <option value="">Todas las categorías</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* View Mode */}
              <div className="flex border border-v2-border rounded overflow-hidden">
                <button 
                  onClick={() => setViewMode('grid')}
                  className={`p-3 transition-colors ${viewMode === 'grid' ? 'bg-v2-orange text-white' : 'bg-v2-gray text-v2-text-muted hover:text-white'}`}
                >
                  <Grid size={20} />
                </button>
                <button 
                  onClick={() => setViewMode('list')}
                  className={`p-3 transition-colors ${viewMode === 'list' ? 'bg-v2-orange text-white' : 'bg-v2-gray text-v2-text-muted hover:text-white'}`}
                >
                  <List size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-7xl mx-auto px-8 py-12">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-v2-gray border border-v2-border rounded animate-pulse">
                <div className="h-56 bg-v2-light-gray" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-v2-light-gray rounded w-3/4" />
                  <div className="h-4 bg-v2-light-gray rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-v2-gray rounded-full flex items-center justify-center mx-auto mb-6">
              <Search size={40} className="text-v2-orange" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">No se encontraron productos</h3>
            <p className="text-v2-text-muted">Intenta con otros términos de búsqueda</p>
          </div>
        ) : (
          <>
            <p className="text-v2-text-muted mb-6">
              Contamos con una gran variedad de productos
              {/*<span className="text-v2-orange font-bold">{filteredProducts.length}</span> producto(s) encontrado(s)*/}
            </p>
            
            {viewMode === 'grid' ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <div 
                    key={product.id}
                    className="group bg-v2-gray border border-v2-border hover:border-v2-orange overflow-hidden transition-all duration-300"
                    data-testid={`product-card-v2-${product.id}`}
                  >
                    <div className="relative overflow-hidden">
                      <Link to={`/producto/${product.seo_url}`}>
                        <img 
                          src={resolveImage(product.ruta_img, PRODUCT_IMAGE_BASE)}
                          alt={product.nombre}
                          className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => { e.target.src = 'https://placehold.co/400x300/1A1A1A/FF6B00?text=NAYACEROS'; }}
                        />
                      </Link>
                      <div className="absolute inset-0 bg-gradient-to-t from-v2-darker via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <Link
                        to={`/producto/${product.seo_url}`}
                        className="absolute bottom-4 right-4 w-12 h-12 bg-v2-orange hover:bg-v2-orange-dark text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300"
                        data-testid={`add-to-cart-v2-${product.id}`}
                      >
                        <Plus size={24} />
                      </Link>
                      {product.categoria && (
                        <span className="absolute top-4 left-4 bg-v2-orange text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                          {product.categoria}
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <Link to={`/producto/${product.seo_url}`}>
                        <h3 className="text-lg font-bold text-white group-hover:text-v2-orange transition-colors uppercase">
                          {product.nombre}
                        </h3>
                      </Link>
                      <p className="text-v2-text-muted text-sm mt-2 line-clamp-2">{product.descripcion}</p>
                      <Link
                        to={`/producto/${product.seo_url}`}
                        className="mt-4 text-v2-orange font-medium text-sm hover:text-v2-orange-light flex items-center gap-2 group/btn"
                      >
                        <Plus size={16} />
                        Ver producto y cotizar
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredProducts.map((product) => (
                  <div 
                    key={product.id}
                    className="group bg-v2-gray border border-v2-border hover:border-v2-orange flex flex-col sm:flex-row overflow-hidden transition-all duration-300"
                    data-testid={`product-card-v2-${product.id}`}
                  >
                    <div className="sm:w-48 h-48 sm:h-auto relative overflow-hidden flex-shrink-0">
                      <Link to={`/producto/${product.seo_url}`}>
                        <img 
                          src={resolveImage(product.ruta_img, PRODUCT_IMAGE_BASE)}
                          alt={product.nombre}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => { e.target.src = 'https://placehold.co/400x300/1A1A1A/FF6B00?text=NAYACEROS'; }}
                        />
                      </Link>
                      {product.categoria && (
                        <span className="absolute top-3 left-3 bg-v2-orange text-white text-xs font-bold px-2 py-1 uppercase tracking-wider">
                          {product.categoria}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 p-6 flex flex-col justify-between">
                      <div>
                        <Link to={`/producto/${product.seo_url}`}>
                          <h3 className="text-xl font-bold text-white group-hover:text-v2-orange transition-colors uppercase">
                            {product.nombre}
                          </h3>
                        </Link>
                        <p className="text-v2-text-muted mt-2">{product.descripcion}</p>
                      </div>
                      <Link
                        to={`/producto/${product.seo_url}`}
                        className="mt-4 self-start bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider px-6 py-3 flex items-center gap-2 transition-colors"
                        style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%)' }}
                      >
                        <Plus size={18} />
                        Ver producto y cotizar
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsV2;
